import { useMemo, useState } from "react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import { useAdminLinkAthlete } from "@/features/admin/hooks/useAdminData";
import type { Athlete, AthleteRelationship } from "@/lib/types";
import { useAdminUserRows } from "../hooks/useAdminDirectoryLookups";
import {
  FIELD_CLASS,
  FIELD_LABEL_CLASS,
  RELATIONSHIP_LABEL,
} from "../athleteDisplay";

const RELATIONSHIP_OPTIONS = (
  ["self", "guardian", "family"] as AthleteRelationship[]
).map((value) => ({ value, label: RELATIONSHIP_LABEL[value] }));

const MAX_CANDIDATES = 20;

/**
 * Link one of the club's accounts to the ATHLETE (D4): the service then
 * projects the link onto every assignment. The picker reads the same
 * `GET /api/users` directory the Users / People pages cache and filters it
 * client-side by username / display name.
 */
export function LinkAccountDialog({
  athlete,
  linkedUserIds,
  onClose,
  onSaved,
}: {
  athlete: Athlete;
  linkedUserIds: ReadonlySet<string>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const usersQuery = useAdminUserRows(true);
  const [search, setSearch] = useState("");
  const [userId, setUserId] = useState<string | null>(null);
  const [relationship, setRelationship] =
    useState<AthleteRelationship>("guardian");
  const [error, setError] = useState<string | null>(null);
  const mut = useAdminLinkAthlete();

  const candidates = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (usersQuery.data ?? [])
      .filter(
        (user) =>
          user.enabled !== false &&
          !linkedUserIds.has(user.id) &&
          (!needle ||
            user.username.toLowerCase().includes(needle) ||
            (user.display_name ?? "").toLowerCase().includes(needle)),
      )
      .slice(0, MAX_CANDIDATES);
  }, [usersQuery.data, search, linkedUserIds]);

  const submit = async () => {
    setError(null);
    if (!userId) {
      setError("Pick an account.");
      return;
    }
    try {
      await mut.mutateAsync({
        athleteId: athlete.id,
        body: { user_id: userId, relationship },
      });
      toast.success("Account linked");
      onSaved();
    } catch (err) {
      const message = errorMessage(err);
      setError(message);
      toast.error("Couldn't link the account", { description: message });
    }
  };

  return (
    <ConfirmDialog
      title="Link an account"
      confirmDisabled={mut.isPending}
      message={
        <div className="flex flex-col gap-2">
          <p className="text-xs text-ink-muted">
            The account gets this athlete&rsquo;s membership on every team the
            athlete is assigned to, now and later.
          </p>
          <label className="flex flex-col gap-1">
            <span className={FIELD_LABEL_CLASS}>Find an account</span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Username or name"
              autoFocus
              className={FIELD_CLASS}
              data-testid="admin-athlete-link-search"
            />
          </label>
          <div
            data-testid="admin-athlete-link-users"
            className="flex max-h-48 flex-col gap-0.5 overflow-auto rounded-inset border border-rule bg-elevated p-1"
          >
            {usersQuery.isLoading ? (
              <p className="px-1 py-1 font-mono text-[0.65rem] text-ink-muted">
                Loading accounts…
              </p>
            ) : candidates.length === 0 ? (
              <p className="px-1 py-1 font-mono text-[0.65rem] text-ink-muted">
                No matching account.
              </p>
            ) : (
              candidates.map((user) => {
                const selected = user.id === userId;
                return (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => setUserId(selected ? null : user.id)}
                    aria-pressed={selected}
                    data-testid="admin-athlete-link-user"
                    data-user-id={user.id}
                    className="flex w-full items-center gap-2 rounded-inset px-1.5 py-1 text-left text-xs transition-colors hover:bg-field aria-pressed:text-accent"
                  >
                    <span className="text-ink">
                      {user.display_name || user.username}
                    </span>
                    <span className="font-mono text-ink-muted">
                      {user.username}
                    </span>
                    {selected ? (
                      <span className="ml-auto text-accent" aria-hidden>
                        ✓
                      </span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
          <label className="flex flex-col gap-1">
            <span className={FIELD_LABEL_CLASS}>Relationship</span>
            <TkticSelect
              value={relationship}
              onChange={(next) => setRelationship(next as AthleteRelationship)}
              options={RELATIONSHIP_OPTIONS}
              size="sm"
              ariaLabel="Relationship to the athlete"
              data-testid="admin-athlete-link-relationship"
            />
          </label>
          {error ? (
            <p
              data-testid="admin-athlete-link-error"
              className="font-mono text-xs text-destructive"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Linking…" : "Link account"}
      onConfirm={() => void submit()}
      onClose={onClose}
      data-testid="admin-athlete-link-dialog"
    />
  );
}
